import React, { useMemo, useState } from 'react';
import { Building2, ChevronDown, Check, Crown, Search, ShieldAlert } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTenant } from '@/contexts/TenantContext';
import { LICENSE_TIER_COLORS } from '@/services/admin';

const STATUS_STYLES = {
  deactivated: 'bg-rose-50 text-rose-700 border-rose-200',
  expired: 'bg-amber-50 text-amber-700 border-amber-200',
  suspended: 'bg-rose-50 text-rose-700 border-rose-200',
};

const TenantItem = ({ tenant, active, onSelect }) => (
  <DropdownMenuItem
    onClick={() => onSelect(tenant)}
    className="flex items-center justify-between cursor-pointer"
    data-testid={`tenant-option-${tenant.id}`}
  >
    <div className="flex items-center gap-2 flex-1 min-w-0">
      {tenant.is_member === false
        ? <ShieldAlert className="w-4 h-4 flex-shrink-0 text-amber-500" />
        : <Building2 className="w-4 h-4 flex-shrink-0 text-gray-400" />}
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{tenant.name}</p>
        <p className="text-xs text-gray-500 truncate">
          {tenant.is_member === false ? 'Super-admin access' : (tenant.user_role || tenant.role || 'member')}
          {tenant.slug ? ` · ${tenant.slug}` : ''}
        </p>
      </div>
    </div>
    <div className="flex items-center gap-1.5 flex-shrink-0">
      {STATUS_STYLES[tenant.status] && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_STYLES[tenant.status]}`}
          data-testid={`tenant-status-${tenant.id}`}>{tenant.status}</span>
      )}
      {tenant.license_tier && (
        <span className={`text-xs px-1.5 py-0.5 rounded border ${LICENSE_TIER_COLORS[tenant.license_tier] || 'bg-gray-100'}`}>
          {tenant.license_tier}
        </span>
      )}
      {active && <Check className="w-4 h-4 text-green-500" />}
    </div>
  </DropdownMenuItem>
);

const TenantSwitcher = ({ variant = 'default' }) => {
  const { tenants, currentTenant, switchTenant, isSuperAdmin, loading } = useTenant();
  const [query, setQuery] = useState('');

  const { mine, others } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (t) => !q || [t.name, t.slug, t.id, ...(t.domains || [])]
      .some((v) => (v || '').toLowerCase().includes(q));
    const filtered = tenants.filter(match);
    return {
      mine: filtered.filter((t) => t.is_member !== false),
      others: filtered.filter((t) => t.is_member === false),
    };
  }, [tenants, query]);

  if (loading) return <div className="animate-pulse h-9 w-32 bg-gray-200 rounded" />;
  if (tenants.length === 0 && !isSuperAdmin) return null;

  if (tenants.length === 1 && !isSuperAdmin) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-md ${variant === 'sidebar' ? 'text-white/80' : 'bg-gray-100'}`}>
        <Building2 className="w-4 h-4" />
        <span className="text-sm font-medium truncate max-w-[150px]">{currentTenant?.name || 'No tenant'}</span>
      </div>
    );
  }

  const showSearch = isSuperAdmin || tenants.length > 6;

  return (
    <DropdownMenu onOpenChange={(open) => { if (!open) setQuery(''); }}>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant === 'sidebar' ? 'ghost' : 'outline'}
          className={`justify-between min-w-[150px] max-w-[200px] ${variant === 'sidebar' ? 'text-white/80 hover:text-white hover:bg-white/10' : ''}`}
          data-testid="tenant-switcher"
        >
          <div className="flex items-center gap-2 truncate">
            <Building2 className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{currentTenant?.name || 'Select tenant'}</span>
          </div>
          <ChevronDown className="w-4 h-4 flex-shrink-0 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[300px] max-w-[calc(100vw-1rem)] p-0 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-xl" data-testid="tenant-switcher-menu">
        {isSuperAdmin && (
          <DropdownMenuLabel className="flex items-center justify-between gap-2 text-amber-600 px-3 pt-2">
            <span className="flex items-center gap-2"><Crown className="w-4 h-4" />Super Admin</span>
            <span className="text-[11px] font-normal text-gray-500" data-testid="tenant-switcher-count">{tenants.length} tenants</span>
          </DropdownMenuLabel>
        )}
        {showSearch && (
          <div className="px-2 py-2 border-b" onKeyDown={(e) => e.stopPropagation()}>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
              <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, slug, domain" className="pl-8 h-9" data-testid="tenant-switcher-search" />
            </div>
          </div>
        )}
        <div className="max-h-[60vh] overflow-y-auto p-1">
          <DropdownMenuLabel className="text-xs text-gray-500">Your Organizations</DropdownMenuLabel>
          {mine.length === 0 && (
            <div className="px-2 py-2 text-xs text-gray-500">{query ? 'No matches' : 'No tenants assigned'}</div>
          )}
          {mine.map((t) => <TenantItem key={t.id} tenant={t} active={currentTenant?.id === t.id} onSelect={switchTenant} />)}
          {isSuperAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-gray-500">All tenants (super-admin access)</DropdownMenuLabel>
              {others.length === 0 && <div className="px-2 py-2 text-xs text-gray-500" data-testid="tenant-switcher-no-others">No matches</div>}
              {others.map((t) => <TenantItem key={t.id} tenant={t} active={currentTenant?.id === t.id} onSelect={switchTenant} />)}
            </>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default TenantSwitcher;
